import React, { useState, useEffect } from "react";
import { X, Loader } from "lucide-react";
import { masterService } from "../../../api/masterService";
import { useNotification } from "../../../contexts/NotificationContext";

export default function LinkAddonGroupModal({
  isOpen,
  onClose,
  productId,
  linkedGroupIds = [],
  onSuccess,
}) {
  const { showSuccess, showError } = useNotification();
  const [groups, setGroups] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelectedIds(linkedGroupIds);
      fetchGroups();
    }
  }, [isOpen, linkedGroupIds]);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const res = await masterService.addonGroups.list({ limit: 100 });
      setGroups(res?.data || []);
    } catch (err) {
      console.error("Error fetching addon groups:", err);
      showError("Gagal mengambil daftar grup pilihan extra");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await masterService.addonGroups.setProductAddonGroups(productId, selectedIds);
      showSuccess("Kaitan grup pilihan extra berhasil diperbarui");
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error("Error linking addon groups:", err);
      showError(err.message || "Gagal menghubungkan grup pilihan extra");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-in fade-in duration-200"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-2xl w-full max-w-md flex flex-col max-h-[85vh] overflow-hidden animate-in zoom-in-95 duration-200"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-teal-600 text-white">
          <h2 className="text-base font-semibold">Hubungkan Grup Pilihan Extra</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          <p className="text-xs text-slate-500 leading-normal mb-3">
            Pilih grup pilihan extra global di bawah ini untuk ditautkan ke produk ini. Pilihan extra di dalam grup ini akan muncul saat kasir memesan produk ini.
          </p>

          {loading ? (
            <div className="py-12 flex flex-col items-center justify-center text-slate-500 text-sm gap-2">
              <Loader className="w-5 h-5 animate-spin text-teal-600" />
              <span>Memuat daftar grup...</span>
            </div>
          ) : (
            <div className="space-y-2">
              {groups.map((group) => {
                const isChecked = selectedIds.includes(group.id);
                return (
                  <label
                    key={group.id}
                    className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl cursor-pointer hover:bg-teal-50/30 hover:border-teal-200 transition-all"
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => handleToggle(group.id)}
                      className="w-4.5 h-4.5 text-teal-600 border-slate-300 rounded focus:ring-teal-500 mt-0.5"
                    />
                    <div className="flex-1 text-sm min-w-0">
                      <div className="font-semibold text-slate-800 truncate">{group.name}</div>
                      {group.description && (
                        <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">
                          {group.description}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-1 mt-2">
                        {(group.addons || []).map((a) => (
                          <span
                            key={a.id}
                            className="inline-flex px-1.5 py-0.5 bg-white border border-slate-200 rounded text-[10px] text-slate-600 font-medium"
                          >
                            {a.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  </label>
                );
              })}

              {groups.length === 0 && (
                <div className="text-slate-400 text-xs text-center py-8 italic">
                  Belum ada grup pilihan extra global yang dibuat. Silakan buat grup pilihan di Master Data.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/40 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 bg-white ring-1 ring-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 font-medium text-sm transition-colors"
          >
            Batal
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium text-sm transition-colors shadow-sm"
          >
            {saving ? "Menghubungkan..." : "Simpan Kaitan"}
          </button>
        </div>
      </div>
    </div>
  );
}
